import { useState } from 'react';
import { Alert, App, Button, Card, Drawer, Form, Space, Typography } from 'antd';
import { SyncOutlined } from '@ant-design/icons';
import type { AdminLocation, CreateLocationBody } from '@goelectric/shared-types';
import { locationApi } from '../api/locationApi';
import { describeLocationApiError } from '../utils/locationErrors';
import type { LocationFormState } from '../utils/locationForm';
import { LocationForm } from './LocationForm';

interface NewLocationDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Notifica la ubicación creada y **sincronizada**, para auto-seleccionarla en el paso 2. */
  onCreated: (location: AdminLocation) => void;
}

/**
 * Aside de alta de Ubicación dentro del wizard de Cargador (TG-125, opción B): reusa el
 * `LocationForm` existente sin modificarlo y resuelve la sincronización **inline**.
 *
 * El alta del cargador exige la ubicación ya sincronizada con el core (`syncStatus`
 * `SYNCED`). El POST de locación puede volver `PENDIENTE`/`ERROR`: en ese caso el aside no
 * se cierra y ofrece reintentar la sincronización (`locationApi.sync`) sin salir del flujo.
 */
export function NewLocationDrawer({ open, onClose, onCreated }: NewLocationDrawerProps) {
  const { message } = App.useApp();
  const [form] = Form.useForm<LocationFormState>();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorField, setErrorField] = useState<string | null>(null);
  // Ubicación creada pero aún sin sincronizar: dispara el aviso inline con el reintento.
  const [pending, setPending] = useState<AdminLocation | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);

  /** Cierra el aside limpiando todo: la próxima apertura arranca en el paso 1 sin restos. */
  const handleClose = () => {
    setError(null);
    setErrorField(null);
    setPending(null);
    setSyncError(null);
    form.resetFields();
    onClose();
  };

  /** Éxito final: crear/sincronizar OK → notifica la ubicación y cierra el aside. */
  const finish = (location: AdminLocation) => {
    message.success('Ubicación creada.');
    onCreated(location);
    handleClose();
  };

  const handleSubmit = async (body: CreateLocationBody) => {
    setSubmitting(true);
    setError(null);
    setErrorField(null);
    try {
      const created = await locationApi.create(body);
      if (created.syncStatus === 'SYNCED') {
        finish(created);
        return;
      }
      // El alta sirvió pero el core no confirmó: el aviso inline ofrece reintentar.
      setPending(created);
    } catch (caught) {
      const { message: detail, field } = describeLocationApiError(caught);
      setError(detail);
      setErrorField(field ?? null);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetrySync = async () => {
    if (!pending) return;
    setSyncing(true);
    setSyncError(null);
    try {
      finish(await locationApi.sync(pending.id));
    } catch (caught) {
      setSyncError(
        describeLocationApiError(caught, 'No se pudo sincronizar la ubicación.').message,
      );
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Drawer
      open={open}
      // Mismo encabezado que la página de alta (`/ubicaciones/nueva`): `Typography.Title`
      // nivel 3 (fuente display, mayúsculas) en vez del título plano de 16px del Drawer.
      title={
        <Typography.Title level={3} style={{ margin: 0 }}>
          Nueva ubicación
        </Typography.Title>
      }
      placement="right"
      // Ancho que da al stepper horizontal el mismo aire que la Card de la página (los
      // cuatro pasos entran en una línea, sin que el primer label envuelva a 720px).
      width={880}
      onClose={handleClose}
      destroyOnHidden
    >
      {error && (
        <Alert
          type="error"
          showIcon
          role="alert"
          message="No se pudo crear la ubicación"
          description={error}
          style={{ marginBottom: 16 }}
        />
      )}

      {pending ? (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Alert
            type="warning"
            showIcon
            message="La ubicación todavía no está sincronizada con el core"
            description={`Se creó "${pending.name}", pero el alta del cargador necesita que la ubicación esté sincronizada. Reintenta la sincronización para continuar.`}
          />
          {syncError && <Alert type="error" showIcon role="alert" message={syncError} />}
          <Space>
            <Button
              type="primary"
              icon={<SyncOutlined />}
              loading={syncing}
              onClick={handleRetrySync}
            >
              Reintentar sincronización
            </Button>
            <Button onClick={handleClose}>Cancelar</Button>
          </Space>
        </Space>
      ) : (
        // Mismo contenedor que la página de alta: la Card aporta la superficie, el padding
        // y el redondeo del wizard (el Drawer por sí solo varía de superficie según el tema).
        <Card>
          <LocationForm
            form={form}
            onSubmit={handleSubmit}
            submitting={submitting}
            errorField={errorField}
          />
        </Card>
      )}
    </Drawer>
  );
}
